import React, {
  useCallback, useContext, useState,
} from 'react';
import {
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import validateForm from 'utils/Validator';
import Form from 'components/Form/Layout';
import { yupUploadDoc } from 'utils/yupFields';
import { LoadingContext } from 'context/Loading';
import logo from 'assets/img/logo-blue-full.png';
import TextField from 'components/Form/FormTextField';
import SuccessMessage from 'components/Modal/SuccessMessage';
import {
  DocRequestEntity, UploadDocForm,
} from 'types/DocRequest';
import { IoArrowForwardCircleOutline } from 'react-icons/io5';
import handlePromise from 'utils/Promise';
import DocsRequestService from 'services/api/DocsRequest';
import InputFile from '../FormInputFile';

type UploadDocModalProps = {
  data?: DocRequestEntity,
  onClose: () => void,
  reloadData: ( newData: DocRequestEntity ) => void,
};

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

function UploadDocModal( {
  data, onClose, reloadData,
}: UploadDocModalProps ) {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );
  const [formSubmitted, setFormSubmitted] = useState( false );
  const validate = validateForm( yupUploadDoc );

  const submitForm = useCallback( async ( formValues: UploadDocForm ) => {
    toggleLoading( true );
    const dataToSend = {
      file: formValues.file?.[0],
      nota: formValues.nota,
    };

    const cuentaId = data?.cuentaDeInversionID;
    const solicitudId = data?.id;
    if ( cuentaId == null || solicitudId == null ) {
      toggleLoading( false );
      return;
    }

    const [, response, responseData] = await handlePromise(
      DocsRequestService.uploadDocRequestFile( cuentaId, solicitudId, dataToSend ),
    );

    if ( response.ok ) {
      setFormSubmitted( true );

      const [, refetchedResponse, refetchedData] = await handlePromise(
        DocsRequestService.getDocRequestsById(
          responseData?.cuentaDeInversionID,
          responseData?.id,
        ),
      );
      if ( refetchedResponse.ok ) {
        reloadData( refetchedData );
      } else {
        reloadData( responseData );
      }
    }
    toggleLoading( false );
    // ToDo show error
  }, [data?.cuentaDeInversionID, data?.id, toggleLoading] );

  return (
    <Modal
      show
      centered
      size="lg"
      onHide={onClose}
      className="table-docs"
    >
      <Modal.Header closeButton className="pb-0" />
      <Modal.Body className="pt-0">
        <div className="text-center">
          <img src={logo} alt="Grupo Impar" height={40} />
        </div>
        <Row className="mt-4">
          <Col md={11} className="m-auto">
            {formSubmitted
              ? (
                <SuccessMessage
                  description={t( 'admin.docs.createSuccess' )}
                  onClose={onClose}
                />
              )
              : (
                <Form
                  validate={validate}
                  onSubmit={( values ) => submitForm( values as UploadDocForm )}
                  initialValues={data}
                  body={(
                    <>
                      <h2 className="text-primary mb-4">{ t( 'admin.docs.upload' )}</h2>

                      <InputFile
                        field="file"
                        placeholder="admin.docs.field.uploadFile"
                        dropzoneOptions={{
                          multiple: true,
                          maxSize: MAX_SIZE,
                          maxFiles: 1,
                          accept: ['.jpg', '.jpeg', '.png', '.heic', '.pdf'],
                        }}
                      />
                      <TextField
                        field="nota"
                        placeholder="admin.docs.field.note"
                      />

                      <Row className="justify-content-end">
                        <Col xs="auto">
                          <Button
                            variant="primary"
                            type="submit"
                            size="sm"
                            className="btn--rounded text--normal text--xmd"
                          >
                            {t( 'admin.common.upload' )}
                            <IoArrowForwardCircleOutline size={20} className="ms-2" />
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}
                />
              )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default UploadDocModal;
